import { Capacitor } from '@capacitor/core';
import { ScreenOrientation } from '@ionic-native/screen-orientation';
import { IonApp, IonRouterOutlet, setupIonicReact } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route } from 'react-router';
import { setConfig } from './features/config/configSlice';
import './i18n/config';
import MobileTabsWrapper from './shared/components/MobileTabsWrapper/MobileTabsWrapper';
import ProtectedRoute from './shared/components/ProtectedRoute/ProtectedRoute';
import { menuItems } from './shared/config/menuItems';
import { DEFAULT_MARKET } from './shared/constants/constants';
import ThemeProvider from './shared/providers/ThemeProvider';
import { loadConfig } from './shared/utils/config';

/* Pages */
import ChangeAddress from './pages/AddressChange';
import ChangeUser from './pages/UserChange';
import Company from './pages/Company';
import CustodyReports from './pages/CustodyReports';
import DataPrivacy from './pages/DataPrivacy';
import Devices from './pages/Devices';
import ErrorPage404 from './pages/ErrorPages/404';
import HelpInfo from './pages/HelpInfo';
import Home from './pages/Home/Home';
import Imprint from './pages/Imprint';
import Markets from './pages/Markets';
import OAuth from './pages/OAuth';
import Password from './pages/Password';
import Transactions from './pages/Transactions';
import UserProfile from './pages/UserProfile';
import Wallet from './pages/Wallet';
import WhistleblowerSystem from './pages/WhistleblowerSystem';
import { clearUserAuthStorage, getStorageItem } from '@/shared/helpers/storage';
import { TOKEN_COOKIE_NAMES } from '@/shared/utils/constants';
import { socket } from '@/shared/websocket/socket';
import { selectIsLoggedIn, setOAuth } from '@/features/oAuth/oAuthSlice';
import { loadFlowConfig } from '@/shared/utils/flowConfig';
import { ConfigProvider } from '@blocknox/flow';
import { useLocale } from '@/shared/helpers/i18n';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/display.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/padding.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';

/* Custom stylesheets */
import './styles/theme/button.css';
import './styles/theme/colors.css';
import './styles/theme/sizes.css';
import './styles/theme/theme.css';
import './styles/theme/typography.css';
import './styles/flow.css';

setupIonicReact({
  platform: {
    /** The default `desktop` function returns false for devices with a touchscreen.
     * This is not always wanted, so this function tests the User Agent instead.
     **/
    desktop: (win) => {
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(win.navigator.userAgent);
      return !isMobile;
    }
  }
});

const App: React.FC = () => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const [isLoadingTokens, setIsLoadingTokens] = useState(true);
  const isApp = Capacitor.isNativePlatform();
  const { locale } = useLocale();
  const flowConfig = useMemo(() => loadFlowConfig(locale), [locale]);

  if (isApp) {
    ScreenOrientation.lock(ScreenOrientation.ORIENTATIONS.PORTRAIT);
  }

  useEffect(() => {
    const config = loadConfig();
    dispatch(setConfig(config));
  }, [dispatch]);

  useEffect(() => {
    const fetchTokens = async () => {
      try {
        const accessToken = await getStorageItem(TOKEN_COOKIE_NAMES['access']);
        const refreshToken = await getStorageItem(TOKEN_COOKIE_NAMES['refresh']);

        if (accessToken && isLoggedIn) {
          if (!socket.isOpen()) {
            socket.open(accessToken);
          }
        }

        dispatch(setOAuth({ accessToken, refreshToken }));
      } catch (error) {
        dispatch(setOAuth({ accessToken: null, refreshToken: null }));
        void clearUserAuthStorage();
      } finally {
        setIsLoadingTokens(false);
      }
    };

    void fetchTokens();
  }, [dispatch, isLoggedIn]);

  return (
    <ConfigProvider config={flowConfig}>
      <ThemeProvider>
        <IonApp>
          <IonReactRouter>
            <MobileTabsWrapper items={menuItems}>
              <IonRouterOutlet animated={false}>
                <Route path="/" exact children={<Home isLoading={isLoadingTokens} />} />
                <Route exact path="/imprint" component={Imprint} />
                <Redirect exact from="/markets" to={`/markets/${DEFAULT_MARKET}`} />
                <ProtectedRoute path="/markets/:market" component={Markets} />
                <Route exact path="/privacy-policies" component={DataPrivacy} />
                <ProtectedRoute path="/wallets" exact component={Wallet} />
                <ProtectedRoute path="/wallets/:asset" component={Wallet} />
                <ProtectedRoute path="/transactions" exact component={Transactions} />
                <Redirect exact from="/settings" to="/settings/personal" />
                <ProtectedRoute path="/settings/personal" component={UserProfile} />
                <ProtectedRoute path="/settings/company" component={Company} />
                <ProtectedRoute path="/settings/user-change" component={ChangeUser} />
                <ProtectedRoute path="/settings/address-change" component={ChangeAddress} />
                <ProtectedRoute path="/settings/custody-reports" component={CustodyReports} />
                <ProtectedRoute path="/settings/password" component={Password} />
                <ProtectedRoute path="/settings/devices" component={Devices} />
                <ProtectedRoute path="/settings/help" component={HelpInfo} />
                <Route exact path="/whistleblower-system" component={WhistleblowerSystem} />
                <Route path="/oauth" component={OAuth} />
                <Route component={ErrorPage404} />
              </IonRouterOutlet>
            </MobileTabsWrapper>
          </IonReactRouter>
        </IonApp>
      </ThemeProvider>
    </ConfigProvider>
  );
};

export default App;
